import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, Award, CheckCircle2, Clock, Compass, Loader2, Lock, Medal, PlayCircle, Share2, Sparkles, BookOpen,
} from "lucide-react";
import { Mascot } from "@/components/lesson/Mascot";
import { Confetti } from "@/components/Confetti";
import {
  courseLessonPath, credentialPath, credentialShareUrl, levelBadge,
  type CourseDetailPayload, type CourseModule,
} from "@/lib/studentCourses";

export default function CourseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [blockedBy, setBlockedBy] = useState<Array<{ slug: string; title: string }> | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  const { data, isLoading, error } = useQuery<CourseDetailPayload>({
    queryKey: ["/api/courses", slug],
    enabled: !!slug,
  });

  const enrollMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/courses/${slug}/enroll`);
      return res.json();
    },
    onSuccess: () => {
      setBlockedBy(null);
      queryClient.invalidateQueries({ queryKey: ["/api/courses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/recommendations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/me/courses"] });
      toast({ title: "You are in!", description: "Start with lesson 1 whenever you are ready." });
    },
    onError: (err: any) => {
      const missing = err?.response?.missingPrerequisites;
      if (err?.status === 409 && Array.isArray(missing) && missing.length > 0) {
        setBlockedBy(missing);
        toast({
          title: "Finish one course first",
          description: "There is a course you need to complete before this one.",
        });
        return;
      }
      toast({ title: "Could not join the course", description: "Check your connection and try again.", variant: "destructive" });
    },
  });

  const course = data?.course;
  const modules = useMemo(() => [...(data?.modules ?? [])].sort((a, b) => a.sortOrder - b.sortOrder), [data]);
  const enrollment = data?.enrollment ?? null;
  const credential = data?.credential ?? null;
  const completed = !!enrollment?.completedAt;

  // Highlight the first lesson still to do. Nothing is locked, this is just a nudge.
  const nextModuleId = useMemo(() => {
    const next = modules.find(m => m.progress?.status !== "completed");
    return next?.id ?? null;
  }, [modules]);

  const missingPrerequisites = blockedBy ?? (data && !data.prerequisitesMet ? data.missingPrerequisites : null);

  const copyCredentialLink = async () => {
    if (!credential) return;
    const url = credentialShareUrl(credential.code);
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied", description: "Paste it anywhere to show off your certificate." });
    } catch {
      window.prompt("Copy your certificate link", url);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
        <div className="container max-w-3xl mx-auto p-4 md:p-6 space-y-4">
          <Skeleton className="h-40 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error || !course) {
    const status = (error as any)?.status;
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-4">
          <Mascot
            mood="oops"
            message={status === 404 ? "Hmm, I cannot find that course." : "Something went wrong loading this course."}
          />
          <Button onClick={() => setLocation("/courses")} className="rounded-xl" data-testid="button-course-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> All courses
          </Button>
        </div>
      </div>
    );
  }

  const level = levelBadge(course.level);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
      {/* ── Hero ── */}
      <div className="bg-gradient-to-r from-primary to-chart-2 text-white p-4 md:p-6">
        <div className="container max-w-3xl mx-auto">
          <Button
            variant="ghost"
            className="text-white hover:bg-white/20 p-2 mb-2"
            onClick={() => setLocation("/courses")}
            data-testid="button-back-courses"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center text-4xl flex-shrink-0">
              {course.emoji || "📘"}
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight">{course.title}</h1>
              {course.description && <p className="text-white/90 mt-1">{course.description}</p>}
              <div className="flex items-center gap-2 flex-wrap mt-3 text-sm">
                <span className="rounded-full bg-white/20 px-3 py-1 font-semibold">{level.label}</span>
                <span className="rounded-full bg-white/20 px-3 py-1 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> {course.estimatedMinutes} min
                </span>
                <span className="rounded-full bg-white/20 px-3 py-1 flex items-center gap-1">
                  <BookOpen className="w-3.5 h-3.5" /> {course.lessonCount} lesson{course.lessonCount === 1 ? "" : "s"}
                </span>
                <span className="rounded-full bg-white/20 px-3 py-1 flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5" /> {course.xpTotal} XP
                </span>
              </div>
              {course.credentialTitle && (
                <p className="flex items-center gap-1.5 text-sm font-semibold mt-3">
                  <Medal className="w-4 h-4" /> Finish it and earn: {course.credentialTitle}
                </p>
              )}
              {course.creatorName && <p className="text-xs text-white/80 mt-2">Made by {course.creatorName}</p>}
            </div>
          </div>
        </div>
      </div>

      <div className="container max-w-3xl mx-auto p-4 md:p-6 space-y-5">
        {/* ── Completed celebration ── */}
        {completed && credential && (
          <>
            <Confetti active duration={3500} particleCount={80} />
            <Card
              className="rounded-2xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/25 animate-in fade-in zoom-in-95 duration-500 fill-mode-both"
              data-testid="card-course-complete"
            >
              <CardContent className="p-5 md:p-6 text-center space-y-3">
                <Award className="w-12 h-12 text-amber-500 mx-auto" />
                <div>
                  <p className="text-xs uppercase tracking-wide font-bold text-amber-700 dark:text-amber-300">Course complete</p>
                  <h2 className="text-2xl font-extrabold">{credential.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    You finished every lesson in {course.title}. Well done!
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 justify-center pt-1">
                  <Button
                    className="rounded-xl font-bold"
                    onClick={() => setLocation(credentialPath(credential.code))}
                    data-testid="button-view-credential"
                  >
                    <Medal className="w-4 h-4 mr-2" /> View credential
                  </Button>
                  <Button variant="outline" className="rounded-xl" onClick={copyCredentialLink} data-testid="button-share-credential">
                    <Share2 className="w-4 h-4 mr-2" /> Share it
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ── Missing prerequisites ── */}
        {missingPrerequisites && missingPrerequisites.length > 0 && (
          <Card className="rounded-2xl border-amber-300 bg-amber-50/70 dark:bg-amber-900/20" data-testid="card-missing-prerequisites">
            <CardContent className="p-4 md:p-5">
              <p className="font-bold flex items-center gap-2">
                <Lock className="w-4 h-4 text-amber-600" /> Finish this first
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Complete {missingPrerequisites.length === 1 ? "this course" : "these courses"} and this one opens up.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {missingPrerequisites.map(prereq => (
                  <Button
                    key={prereq.slug}
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    onClick={() => setLocation(`/courses/${prereq.slug}`)}
                    data-testid={`link-prerequisite-${prereq.slug}`}
                  >
                    {prereq.title}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Enrol / progress ── */}
        {!enrollment ? (
          <Card className="rounded-2xl border-primary/30" data-testid="card-enroll">
            <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <Mascot mood="happy" size="md" className="flex-1" message="Join the course and your progress saves as you go." />
              <Button
                size="lg"
                className="rounded-xl font-bold flex-shrink-0"
                onClick={() => enrollMutation.mutate()}
                disabled={enrollMutation.isPending}
                data-testid="button-enroll"
              >
                {enrollMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Compass className="w-4 h-4 mr-2" />}
                Enroll
              </Button>
            </CardContent>
          </Card>
        ) : !completed ? (
          <Card className="rounded-2xl" data-testid="card-course-progress">
            <CardContent className="p-5">
              <div className="flex justify-between text-sm font-semibold mb-2">
                <span>{enrollment.completedLessons} of {enrollment.totalLessons} lessons done</span>
                <span>{Math.round(enrollment.progress)}%</span>
              </div>
              <Progress value={enrollment.progress} className="h-3 rounded-full" />
              <p className="text-xs text-muted-foreground mt-2">Keep going, you are building towards {course.credentialTitle}.</p>
            </CardContent>
          </Card>
        ) : null}

        {/* ── Lessons ── */}
        <section className="space-y-3">
          <h2 className="text-xl font-extrabold">Lessons in this course</h2>
          {modules.length === 0 ? (
            <Card className="rounded-2xl border-dashed">
              <CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="text-modules-empty">
                Lessons are being added to this course. Check back soon!
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {modules.map((module, i) => (
                <ModuleRow
                  key={module.id}
                  module={module}
                  position={i + 1}
                  isNext={module.id === nextModuleId && !!enrollment}
                  canOpen={!!enrollment}
                  onOpen={() => setLocation(courseLessonPath(module.lesson.slug, course.slug))}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ModuleRow({
  module,
  position,
  isNext,
  canOpen,
  onOpen,
}: {
  module: CourseModule;
  position: number;
  isNext: boolean;
  canOpen: boolean;
  onOpen: () => void;
}) {
  const status = module.progress?.status ?? "not_started";
  const actionLabel = status === "completed" ? "Review" : status === "in_progress" ? "Continue" : "Start";

  return (
    <Card
      className={`rounded-2xl transition-colors ${isNext ? "border-primary/50 bg-primary/5" : ""}`}
      data-testid={`row-module-${module.lesson.slug}`}
    >
      <CardContent className="p-4 flex items-start gap-3">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-sm flex-shrink-0 ${
            status === "completed" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-primary/10 text-primary"
          }`}
        >
          {status === "completed" ? <CheckCircle2 className="w-5 h-5" /> : position}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-base leading-tight">
              {module.lesson.emoji ? `${module.lesson.emoji} ` : ""}{module.lesson.title}
            </h3>
            {isNext && <Badge className="bg-primary/15 text-primary border-0">Next up</Badge>}
            {!module.required && <Badge variant="secondary" className="text-[11px]">Optional</Badge>}
          </div>
          {module.lesson.description && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{module.lesson.description}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2 flex-wrap">
            {typeof module.lesson.estimatedMinutes === "number" && (
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {module.lesson.estimatedMinutes} min</span>
            )}
            {typeof module.lesson.xpReward === "number" && (
              <span className="flex items-center gap-1 text-primary font-semibold"><Sparkles className="w-3.5 h-3.5" /> +{module.lesson.xpReward} XP</span>
            )}
            {status === "completed" ? (
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                Completed{typeof module.progress?.score === "number" ? ` · ${module.progress.score} right` : ""}
              </span>
            ) : status === "in_progress" ? (
              <span className="text-blue-600 dark:text-blue-400 font-semibold">In progress</span>
            ) : (
              <span>Not started</span>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant={status === "completed" ? "outline" : isNext ? "default" : "outline"}
          className="rounded-xl flex-shrink-0"
          onClick={onOpen}
          disabled={!canOpen}
          title={canOpen ? undefined : "Enroll to start this lesson"}
          data-testid={`button-lesson-${module.lesson.slug}`}
        >
          <PlayCircle className="w-4 h-4 mr-1.5" /> {actionLabel}
        </Button>
      </CardContent>
    </Card>
  );
}
